import React, { useCallback, useState } from "react";
import jsonExport from "jsonexport/dist";

import { useMediaQuery, Typography } from "@material-ui/core";

import {
  downloadCSV,
  useListContext,
  BulkExportButton,
  Button,
  DateField,
  DateInput,
  FilterButton,
  FunctionField,
  List,
  RefreshButton,
  SimpleList,
  TextField,
  TopToolbar,
} from "react-admin";

import { ExportIcon, LaunchIcon } from "../../../components/common/iconography";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { getYardCalendarDaysInclusive } from "../../../utils/dateUtils";
import { Pagination } from "../../../components/Pagination";
import { SearchField } from "../../../components/common/SearchField";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { CarrierType, OutboundStatus } from "../../../utils/constants";
import {
  deepRedirect,
  exportCustomField,
  getBackPath,
} from "../../../utils/utils";
import { BackButton } from "../../../components/common/BackButton";
import { AccountFilter, getCustomFieldColumns } from "../ReportsUtils";
import Title from "../Title";

// The placed time: placementDateTime, falling back to arrivalDateTime for
// records placed before the placement backfill ran (the old "Time Placed"
// input stored there).
const placedTime = (outbound) =>
  outbound.placementDateTime || outbound.arrivalDateTime;

// Demurrage runs placed through released, both days counted. Rows saved
// before the clock was stored count the same way here.
const demurrageDays = (outbound) =>
  outbound.daysOnDemurrageClock ||
  getYardCalendarDaysInclusive(
    placedTime(outbound),
    outbound.departureDateTime,
  ) ||
  0;

const exporter = (outbounds) => {
  const customHeaderMap = new Map();

  const outboundsForExport = outbounds.map((outbound) => {
    const outboundForExport = {};
    outboundForExport.railCarNumber = outbound.railCarNumber;
    outboundForExport.carrierName = outbound.carrierName;
    outboundForExport.placedDateTime = new Date(
      placedTime(outbound),
    ).toLocaleDateString();
    outboundForExport.departureDateTime = new Date(
      outbound.departureDateTime,
    ).toLocaleDateString();
    outboundForExport.timeOnClock = demurrageDays(outbound);

    // Custom fields carry their own name in the stored value, so the export
    // headers come off the records rather than the account definitions.
    outbound.customFields?.forEach((field) =>
      exportCustomField(field, outboundForExport, customHeaderMap),
    );

    return outboundForExport;
  });

  jsonExport(
    outboundsForExport,
    {
      // Renaming is positional: an outbound has no notify time, so the names
      // line up one per exported key.
      rename: [
        "Car/Trl Number",
        "Carrier Name",
        "Time Placed",
        "Time Released",
        "Time on Clock (Days)",
      ],
    },
    (err, csv) => {
      downloadCSV(csv, `outboundDemurrage_${new Date().toLocaleDateString()}`); // download as 'outboundDemurrage.csv` file
    },
  );
};

const OutboundSearchField = (props) => {
  const { label, alwaysOn } = props;
  const { displayedFilters, filterValues, setFilters } = useListContext(props);

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { railCarNumber: { matchPhrasePrefix: `${value}` } },
            { railCarNumber: { wildcard: `*${value}*` } },
            { pickupNumber: { matchPhrasePrefix: `${value}` } },
            { pickupNumber: { wildcard: `*${value}*` } },
            { carrierName: { matchPhrasePrefix: `${value}` } },
            // Legacy rows the admin carrier backfill has not reached yet still
            // carry the name on the old `carrier` scalar.
            { carrier: { matchPhrasePrefix: `${value}` } },
          ],
        },
        displayedFilters,
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (onAccountChange) => [
  <AccountFilter
    source="accountId.eq"
    onAccountChange={onAccountChange}
    alwaysOn
  />,
  <OutboundSearchField label="Search" alwaysOn />,
  <DateInput
    source="arrivalDateTime.lte"
    label="Placed Before..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
  <DateInput
    source="arrivalDateTime.gte"
    label="Placed After..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
  <DateInput
    source="departureDateTime.lte"
    label="Released Before..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
  <DateInput
    source="departureDateTime.gte"
    label="Released After..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
];

const ListActions = (props) => {
  return (
    <TopToolbar>
      <FilterButton />
      <RefreshButton />
      <BackButton basePath="/reports" />
    </TopToolbar>
  );
};

const BulkListActions = (props) => (
  <BulkExportButton color="defalut" icon={<ExportIcon />} />
);

export const OutboundDemurrageList = (props) => {
  const defaultSortOrder = { field: "departureDateTime", order: "ASC" };
  const initFilterValues = {
    searchOutboundShipments: {
      reloadYardId: { eq: window.appUser?.reloadYardId },
      status: { eq: OutboundStatus.RELEASED },
      carrierType: { eq: CarrierType.RAIL },
    },
  };
  // The account itself, not just the filtered id: its custom field
  // definitions name the selectable custom field columns.
  const [account, setAccount] = useState(null);
  const handleAccountChange = useCallback(
    (selectedAccount) => setAccount(selectedAccount || null),
    [],
  );

  return (
    <List
      {...props}
      title={<ForceTitle title="Outbound Demurrage" />}
      filter={initFilterValues}
      filters={getListFilters(handleAccountChange)}
      sort={defaultSortOrder}
      actions={<ListActions />}
      bulkActionButtons={<BulkListActions />}
      exporter={exporter}
      pagination={<Pagination />}
    >
      <ShipmentGrid
        account={account}
        history={props.history}
        backPath={getBackPath(props)}
      />
    </List>
  );
};

const ShipmentGrid = ({ account, history, backPath, ...props }) => {
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  // Only offer the custom field columns while an account filter is applied;
  // outbound shipments store values from outboundCustomFields.
  const accountFields =
    account?.outboundCustomFields?.length > 0
      ? account.outboundCustomFields
      : [];

  return isXSmall ? (
    <SimpleList
      {...props}
      primaryText={(record) => record.railCarNumber}
      secondaryText={(record) => (
        <>
          <Typography>{`Placed: ${new Date(
            placedTime(record),
          ).toLocaleDateString()}`}</Typography>
          <Typography color="textSecondary">{`Released: ${new Date(
            record.departureDateTime,
          ).toLocaleDateString()}`}</Typography>
        </>
      )}
      tertiaryText={(record) => `${demurrageDays(record)} Days`}
      // Same Open button as the desktop grid, for the same reason: the row's
      // own link would navigate without the deepRedirect state, and the
      // shipment's Back button would return to the outbound list, not here.
      rightIcon={(record) => (
        <Button
          label="Open"
          onClick={() =>
            deepRedirect(
              history,
              `/outboundShipments/${record.id}`,
              backPath,
              {},
            )
          }
        >
          <LaunchIcon />
        </Button>
      )}
      linkType={false}
    />
  ) : (
    <CustomizableDatagrid
      rowClick={false}
      {...props}
      storageProp={`OutboundDemurrageList_${account?.id || "all"}_large`}
      defaultColumns={[
        "railCarNumber",
        "carrierName",
        "arrivalDateTime",
        "departureDateTime",
        "daysOnDemurrageClock",
      ]}
    >
      {[
        <TextField
          key="railCarNumber"
          source="railCarNumber"
          label="Car/Trl No"
          sortable={true}
        />,
        <TextField
          key="carrierName"
          source="carrierName"
          label="Carrier Name"
          sortable={true}
        />,
        <FunctionField
          key="arrivalDateTime"
          source="arrivalDateTime"
          label="Time Placed"
          render={(record) =>
            placedTime(record)
              ? new Date(placedTime(record)).toLocaleString()
              : ""
          }
          sortable={true}
        />,
        <DateField
          key="departureDateTime"
          source="departureDateTime"
          label="Time Released"
          showTime={true}
          sortable={true}
        />,
        <FunctionField
          key="daysOnDemurrageClock"
          source="daysOnDemurrageClock"
          label="Time on Clock"
          render={(record) => `${demurrageDays(record)} Days`}
          sortable={true}
        />,
        ...getCustomFieldColumns(accountFields),
        // No source, so the column selector can't hide it: the row itself is
        // no longer clickable, this button is the only way into the shipment.
        // deepRedirect rather than a plain link so the shipment's Back button
        // returns to this report instead of the outbound list.
        <FunctionField
          key="open"
          label=""
          render={(record) => (
            <Button
              label="Open"
              onClick={() =>
                deepRedirect(
                  history,
                  `/outboundShipments/${record.id}`,
                  backPath,
                  {},
                )
              }
            >
              <LaunchIcon />
            </Button>
          )}
        />,
      ]}
    </CustomizableDatagrid>
  );
};
